import { useCallback, useEffect, useRef, useState } from 'react';

import { EegRawDataUnavailable, fetchEegRawData } from '@/features/sessions/api';
import type { SessionSummary } from '@/features/sessions/model';
import { hasRawSuffix, RAW_FILE_SUFFIX, type SampleStats } from './decoder';
import type { DecodeRequest, DecodeResponse } from './workers/rawData.worker';

/**
 * Port of `EegRawDataCubit` — same five states, same ordering of checks:
 *
 *   1. the raw file id is the only source; no timeline fallback
 *   2. metadata, then 3. the extension check, then 4. the bytes
 *   5. gunzip, then decode
 */
export type RawEegStatus = 'initial' | 'loading' | 'loaded' | 'unavailable' | 'failure';

export interface RawEegData {
  /** The `.raw.bin` name, i.e. the stored name with `.gz` stripped. */
  fileName: string;
  /** Truncated rendering shown on screen (a huge session would jank the view). */
  preview: string;
  /** Every decoded sample, comma separated — what Copy hands back. */
  fullText: string;
  /** The decoded samples themselves. */
  samples: Int16Array;
  /** The decompressed `.raw.bin` payload, as uploaded. */
  binary: ArrayBuffer;
  byteCount: number;
  sampleCount: number;
  stats: SampleStats;
}

export interface RawEegState {
  status: RawEegStatus;
  data: RawEegData | null;
  message: string | null;
  /** Progress hint while the worker is busy, for long decodes. */
  phase: 'downloading' | 'decoding' | null;
}

const INITIAL: RawEegState = { status: 'initial', data: null, message: null, phase: null };

export function useRawEeg(session: SessionSummary | null | undefined) {
  const [state, setState] = useState<RawEegState>(INITIAL);
  const workerRef = useRef<Worker | null>(null);
  const runId = useRef(0);

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    },
    [],
  );

  const load = useCallback(async (target: SessionSummary) => {
    const currentRun = ++runId.current;
    const isStale = () => runId.current !== currentRun;

    // Step 1 — the raw file id is the only source. No timeline fallback.
    const fileId = target.rawFileId;
    if (fileId === null || fileId.trim() === '') {
      setState({
        status: 'unavailable',
        data: null,
        message: 'This session has no raw EEG data file.',
        phase: null,
      });
      return;
    }

    setState({ status: 'loading', data: null, message: null, phase: 'downloading' });

    let file: { name: string; bytes: ArrayBuffer };
    try {
      // Steps 2 & 3 — metadata, extension check, then the bytes.
      file = await fetchEegRawData(fileId);
    } catch (error) {
      if (isStale()) return;
      if (error instanceof EegRawDataUnavailable) {
        setState({ status: 'unavailable', data: null, message: error.message, phase: null });
        return;
      }
      console.warn('fetchEegRawData failed:', error);
      setState({
        status: 'failure',
        data: null,
        message: !navigator.onLine ? 'No internet connection.' : 'Could not load the file.',
        phase: null,
      });
      return;
    }

    if (isStale()) return;

    if (!hasRawSuffix(file.name)) {
      setState({
        status: 'unavailable',
        data: null,
        message: `Unexpected file "${file.name}". Only ${RAW_FILE_SUFFIX} files can be opened here.`,
        phase: null,
      });
      return;
    }

    setState({ status: 'loading', data: null, message: null, phase: 'decoding' });

    // Steps 4 & 5 — gunzip and decode, on a worker.
    workerRef.current?.terminate();
    const worker = new Worker(new URL('./workers/rawData.worker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      if (isStale()) return;

      const result = event.data;
      if (!result.ok) {
        setState({ status: 'failure', data: null, message: result.message, phase: null });
        return;
      }

      setState({
        status: 'loaded',
        message: null,
        phase: null,
        data: {
          fileName: result.fileName,
          preview: result.preview,
          fullText: result.fullText,
          samples: result.samples,
          binary: result.binary,
          byteCount: result.byteCount,
          sampleCount: result.sampleCount,
          stats: result.stats,
        },
      });
    };

    worker.onerror = (event) => {
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      if (isStale()) return;
      console.warn('EEG decode worker failed:', event.message);
      setState({
        status: 'failure',
        data: null,
        message: 'Could not decode the raw data file.',
        phase: null,
      });
    };

    const request: DecodeRequest = { fileName: file.name, bytes: file.bytes };
    worker.postMessage(request, [file.bytes]);
  }, []);

  useEffect(() => {
    if (!session) {
      setState(INITIAL);
      return;
    }
    void load(session);
  }, [session, load]);

  const reload = useCallback(() => {
    if (session) void load(session);
  }, [session, load]);

  return { ...state, reload };
}
